import { useState } from "react";
import { motion } from "framer-motion";
import ApplyButton from "./ApplyButton";

const FREQUENCIES = [
  { key: "weekly", label: "Weekly", factor: 0.04231, per: "/wk" },
  { key: "biweekly", label: "Bi-weekly", factor: 0.08462, per: "/2 wks" },
  { key: "monthly", label: "Monthly", factor: 0.18333, per: "/mo" },
];

const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function LeaseCalculator() {
  const [price, setPrice] = useState(699);
  const [frequency, setFrequency] = useState("monthly");

  const active = FREQUENCIES.find((f) => f.key === frequency);
  const payment = price * active.factor;

  return (
    <section className="bg-alt py-24" data-testid="lease-calculator">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <span className="text-sm font-bold uppercase tracking-widest text-crimson">Estimate your lease</span>
            <h2 className="mt-2 font-display text-4xl font-black tracking-tighter text-ink sm:text-5xl">
              See your estimated payment in seconds
            </h2>
            <p className="mt-5 max-w-md text-lg text-muted-foreground">
              Slide to match your iPhone's price and pick a payment frequency. This is an estimate — get your exact, personalized quote when you apply through Payvantage.
            </p>

            <div className="mt-10 space-y-8">
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-ink">Device price</label>
                  <span className="font-display text-lg font-black text-ink" data-testid="calc-price-value">${price}</span>
                </div>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="25"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                  data-testid="calc-price-slider"
                  className="mt-3 w-full accent-crimson"
                />
                <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>$200</span><span>$1,500</span></div>
              </div>

              <div>
                <label className="text-sm font-bold text-ink">Payment frequency</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {FREQUENCIES.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setFrequency(f.key)}
                      data-testid={`calc-frequency-${f.key}`}
                      className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${frequency === f.key ? "border-crimson bg-crimson text-white" : "border-line bg-white text-ink hover:border-ink"}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl bg-surface-dark p-10 text-white"
          >
            <div className="text-sm font-bold uppercase tracking-widest text-white/50">Estimated payment</div>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-display text-7xl font-black text-crimson" data-testid="calc-payment">${fmt(payment)}</span>
              <span className="mb-3 text-lg font-bold text-white/60">{active.per}</span>
            </div>
            <div className="mt-6 space-y-3 border-t border-white/10 pt-6 text-sm">
              <div className="flex justify-between"><span className="text-white/60">Device price</span><span className="font-bold">${price}</span></div>
              <div className="flex justify-between"><span className="text-white/60">Payment frequency</span><span className="font-bold">{active.label}</span></div>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <ApplyButton label="Apply & get your real quote" testId="calc-apply-btn" className="justify-center w-full" />
            </div>
            <p className="mt-4 text-xs leading-relaxed text-white/40">
              Estimate only. Actual terms, payments, and fees are determined by Payvantage upon approval and subject to an eligibility check.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
